import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { logger } from './utils/logger.js';
import { config } from './config.js';
import CatalogHybrid from './components/CatalogHybrid';
import HomePage from './components/HomePage';
import GlobalHeader from './components/GlobalHeader';
import BottomNavigation from './components/BottomNavigation';
import CartComponent from './cart';
import ProfilePage from './ProfilePage';
import WelcomeBanner from './WelcomeBanner';
import ProductDetailsModal from './components/ProductDetailsModal';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import PaymentStatusPage from './components/PaymentStatusPage';
import botAPI, { APIUtils } from './botAPI.js';
import CartStorage from './cartStorage.js';
import { Platform } from './utils/platform';
import AuthManager from './utils/authManager.js';
import { ROUTE_MAP, PATH_TO_STEP } from './utils/navigation';
// LandingPage удалён — контент перенесён в HomePage (гостевой режим)

// Standalone login screen for browser users (not in Telegram/Max)
const StandaloneLogin = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState('choice'); // 'choice' | 'email-login' | 'email-register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const API_BASE = ''; // относительный /api (same-origin); window.__ENV__ не инжектился — убран
  const YANDEX_CLIENT_ID = config.YANDEX_CLIENT_ID || '';

  // Handle Yandex OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;

    // Clean URL
    window.history.replaceState({}, '', window.location.pathname);

    setLoading(true);
    fetch(`${API_BASE}/api/auth/yandex`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.access_token) {
          AuthManager.setTokens(data.access_token, data.refresh_token);
          Platform.saveAuthToken(data.access_token);
          onLoginSuccess(data.user);
        } else {
          setError(data.detail || 'Ошибка авторизации через Яндекс');
        }
      })
      .catch(() => setError('Ошибка соединения с сервером'))
      .finally(() => setLoading(false));
  }, []);

  const handleYandexLogin = () => {
    if (!YANDEX_CLIENT_ID) {
      setError('Яндекс OAuth не настроен');
      return;
    }
    const redirectUri = window.location.origin + '/login';
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: YANDEX_CLIENT_ID,
      redirect_uri: redirectUri,
    });
    window.location.href = `https://oauth.yandex.ru/authorize?${params}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const endpoint = mode === 'email-register' ? '/api/auth/register' : '/api/auth/login';
    try {
      const resp = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mode === 'email-register'
          ? { email, password, first_name: firstName, last_name: lastName, phone }
          : { email, password }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.detail || 'Ошибка');
        return;
      }

      if (mode === 'email-register') {
        setMessage('Письмо для подтверждения отправлено на вашу почту. Проверьте входящие.');
        setMode('email-login');
        return;
      }

      // Login success
      if (data.access_token) {
        AuthManager.setTokens(data.access_token, data.refresh_token);
        Platform.saveAuthToken(data.access_token);
        onLoginSuccess(data.user);
      }
    } catch {
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-4 bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="text-gray-600 dark:text-gray-300 mt-4">Авторизация...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-4 bg-gray-50 dark:bg-gray-900">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={new URL('./assets/icons/fish-logo.png', import.meta.url).href} alt="Путь рыбы" className="w-16 h-16 mx-auto mb-3 object-contain" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Путь рыбы</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Коллективные закупки морепродуктов</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}
          {message && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm">
              {message}
            </div>
          )}

          {mode === 'choice' && (
            <div className="space-y-3">
              <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-2">Войдите для оформления заказов</p>

              {YANDEX_CLIENT_ID && (
                <button
                  onClick={handleYandexLogin}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30 text-gray-800 dark:text-gray-200 font-medium transition-colors border border-yellow-200 dark:border-yellow-800 cursor-pointer"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#FC3F1D"/><path d="M13.32 7.67h-.98c-1.5 0-2.28.86-2.28 1.96 0 1.26.54 1.84 1.62 2.56l.9.58-2.58 3.9H8.36l2.28-3.42c-1.32-.9-2.1-1.86-2.1-3.5 0-2.04 1.44-3.42 3.66-3.42h2.44v10.34h-1.32V7.67z" fill="#fff"/></svg>
                  Войти с Яндекс ID
                </button>
              )}

              <button
                onClick={() => setMode('email-login')}
                className="w-full px-4 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold transition-colors cursor-pointer shadow-lg shadow-sky-500/25"
              >
                Войти по email
              </button>

              <button
                onClick={() => setMode('email-register')}
                className="w-full px-4 py-3.5 rounded-xl border-2 border-sky-500 text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 font-semibold transition-colors cursor-pointer"
              >
                Зарегистрироваться
              </button>

              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => onLoginSuccess(null)}
                  className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-sky-600 dark:hover:text-sky-400 transition-colors cursor-pointer py-2"
                >
                  Продолжить без авторизации
                </button>
              </div>
            </div>
          )}

          {(mode === 'email-login' || mode === 'email-register') && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <h3 className="text-lg font-semibold text-center text-gray-900 dark:text-gray-100 mb-1">
                {mode === 'email-register' ? 'Регистрация' : 'Вход по email'}
              </h3>
              {mode === 'email-register' && (
                <>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Имя</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="Иван"
                        required
                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none shadow-sm transition-shadow"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Фамилия</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Иванов"
                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none shadow-sm transition-shadow"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Телефон</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => {
                        let d = e.target.value.replace(/\D/g, '');
                        if (d.startsWith('8')) d = '7' + d.slice(1);
                        if (d.length > 0 && !d.startsWith('7')) d = '7' + d;
                        d = d.slice(0, 11);
                        let f = '';
                        if (d.length > 0) f = '+' + d[0];
                        if (d.length > 1) f += ' (' + d.slice(1, 4);
                        if (d.length > 4) f += ') ' + d.slice(4, 7);
                        if (d.length > 7) f += '-' + d.slice(7, 9);
                        if (d.length > 9) f += '-' + d.slice(9, 11);
                        setPhone(f);
                      }}
                      placeholder="+7 (999) 123-45-67"
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none shadow-sm transition-shadow"
                    />
                  </div>
                </>
              )}
              <div>
                {mode === 'email-register' && <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Email</label>}
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={mode === 'email-register' ? 'your@email.com' : 'Email'}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none shadow-sm transition-shadow"
                />
              </div>
              <div>
                {mode === 'email-register' && <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Пароль</label>}
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'email-register' ? 'Минимум 8 символов' : 'Пароль'}
                  required
                  minLength={8}
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none shadow-sm transition-shadow"
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold transition-colors cursor-pointer shadow-lg shadow-sky-500/25"
              >
                {mode === 'email-register' ? 'Создать аккаунт' : 'Войти'}
              </button>
              <button
                type="button"
                onClick={() => { setMode('choice'); setError(null); }}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Назад
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Главный компонент приложения
const App = () => {
  // 1. Состояние корзины
  const [cartItems, setCartItems] = useState([]);
  const [isCartLoaded, setIsCartLoaded] = useState(false);
  // 2. Этап (шаг) пользователя: 'home' | 'catalog' | 'cart' | 'profile'
  const [step, setStep] = useState('home');
  // 4. Состояние загрузки и ошибок
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // 5. Данные пользователя Telegram
  const [telegramUser, setTelegramUser] = useState(null);

  // Parse referral code from URL (?ref=CODE)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const refCode = params.get('ref');
      if (refCode && refCode.length <= 10) {
        sessionStorage.setItem('pending_referral', refCode);
        // Clean ref from URL without triggering reload (keep other params like ?code= for OAuth)
        params.delete('ref');
        const newUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        console.log('Referral code saved:', refCode);
      }
    } catch (e) {
      // Ignore
    }
  }, []);
  // 6. Все продукты для рекомендаций
  const [allProducts, setAllProducts] = useState([]);
  // 6b. Сториз (Instagram-style, из API)
  const [stories, setStories] = useState([]);
  // 7. Баланс пользователя (загружается параллельно)
  const [userBalance, setUserBalance] = useState(null);
  // 7b. Первый заказ (для приветственной скидки в корзине)
  const [isFirstOrder, setIsFirstOrder] = useState(false);
  // 8. Категории продуктов
  const [categories, setCategories] = useState([]);
  // 9. Прогресс для GlobalHeader (mock данные)
  const [progress, setProgress] = useState({ percent: 0, families: 0 });
  // 10. Выбранный товар для модального окна
  const [selectedProduct, setSelectedProduct] = useState(null);
  // 11. Данные глобального цикла (даты, тема, статус)
  const [cycleInfo, setCycleInfo] = useState(null);
  // 12. Standalone login gate (для пользователей вне Telegram/Max)
  const [needsLogin, setNeedsLogin] = useState(false);

  // Router integration for standalone web (URL-based navigation)
  const routerNavigate = useNavigate();
  const location = useLocation();
  const isRouterMode = Platform.isStandalone();
  const currentPage = isRouterMode ? (PATH_TO_STEP[location.pathname] || 'home') : step;
  const navigateTo = useCallback((page) => {
    window.scrollTo(0, 0);
    if (isRouterMode) {
      routerNavigate(ROUTE_MAP[page] || '/');
    } else {
      setStep(page);
    }
  }, [isRouterMode, routerNavigate]);

  // Глобальный цикл - валидация района не требуется

  // Инициализация Telegram Web App и API
  useEffect(() => {
    const initializeApp = async () => {
      try {
        logger.log('🚀 Инициализация приложения...');

        // Инициализация Platform API (работает в Telegram, Max и standalone)
        Platform.init();

        // Simplified platform detection (CSS no longer depends on data-platform)
        // Just set mobile flag for any responsive behavior that might need it
        document.body.dataset.mobile = Platform.isMobile() ? 'true' : 'false';
        logger.log('📱 Mobile:', Platform.isMobile(), 'Platform:', Platform.getPlatformType());

        // JWT auth flow для Telegram и Max
        if (Platform.isTelegram() || Platform.isMax()) {
          try {
            const initData = Platform.getInitData();
            if (initData) {
              const authResult = await botAPI.authInit(Platform.getPlatformType(), initData);
              if (authResult?.success && authResult?.token) {
                Platform.saveAuthToken(authResult.token);
                // Сохраняем внутренний user_id для API-вызовов (критично для Max)
                if (authResult.user?.user_id) {
                  Platform.saveInternalUserId(authResult.user.user_id);
                }
                logger.log('🔐 JWT token получен');
                // Apply pending referral after successful platform auth
                applyPendingReferral();
              }
            }
          } catch (authErr) {
            console.warn('⚠️ JWT auth не удался (продолжаем без токена):', authErr);
          }
        } else if (Platform.isStandalone()) {
          // Standalone browser — check for existing token or Yandex OAuth callback
          AuthManager.migrateFromLegacy();
          const existingToken = Platform.getAuthToken();
          const hasOAuthCode = new URLSearchParams(window.location.search).has('code');

          if (hasOAuthCode && !existingToken) {
            // OAuth callback — need StandaloneLogin to process the code
            setNeedsLogin(true);
            setIsLoading(false);
            return;
          }
          // No gate for unauthenticated users — they'll see landing page or browse as guests
        }

        // Получаем данные пользователя
        const user = Platform.getUser();
        setTelegramUser(user);

        // Проверяем соединение с API


       const isConnected = await APIUtils.checkConnection();
        if (!isConnected) {
          setError('Не удается подключиться к серверу');
          return;
        }

        // Загружаем категории (для HomePage)
        try {
          const categoriesData = await botAPI.getCategories();
          setCategories(categoriesData.categories || []);
          logger.log('✅ Загружено категорий:', categoriesData.categories?.length || 0);
        } catch (err) {
          console.warn('⚠️ Не удалось загрузить категории:', err);
          setCategories([]);
        }

        // Загружаем данные глобального цикла (тема, даты, статус)
        try {
          const cycleData = await botAPI.getGlobalCycle();
          if (cycleData?.success && cycleData?.cycle) {
            setCycleInfo(cycleData.cycle);
            logger.log('✅ Загружен цикл:', cycleData.cycle.cycle_id, cycleData.cycle.theme_display_name);
          }
        } catch (err) {
          console.warn('⚠️ Не удалось загрузить данные цикла:', err);
        }

        // Загружаем прогресс для глобального цикла (для HomePage виджета)
        try {
          const districtsData = await botAPI.getDistricts();
          const districts = districtsData.districts || [];

          if (districts.length > 0) {
            // Берём первый район (для глобального цикла все районы имеют одинаковые данные)
            const firstDistrict = districts[0];
            setProgress({
              percent: Math.round(firstDistrict.progress || 0),
              families: firstDistrict.participants_count || 0,
              targetFamilies: firstDistrict.target_participants || 0
            });
            logger.log('✅ Загружен прогресс цикла:', {
              percent: firstDistrict.progress,
              participants: firstDistrict.participants_count,
              target: firstDistrict.target_participants
            });
          }
        } catch (err) {
          console.warn('⚠️ Не удалось загрузить прогресс:', err);
        }

        // Мигрируем старые корзины на новую структуру
        CartStorage.migrateOldCarts();

        // Загружаем баланс и товары параллельно (оба нужны до рендера)
        const balancePromise = user?.id
          ? botAPI.getUserBalance(user.id, false).catch(err => {
              console.warn('⚠️ Не удалось загрузить баланс:', err);
              return null;
            })
          : Promise.resolve(null);

        const productsPromise = botAPI.getProducts().catch(err => {
          console.error('❌ Ошибка загрузки товаров:', err);
          return { products: [] };
        });

        logger.log('🔄 Загрузка товаров и баланса...');
        const [balanceResponse, productsData] = await Promise.all([balancePromise, productsPromise]);

        // Баланс
        if (balanceResponse?.success && balanceResponse?.balance !== undefined) {
          setUserBalance({ current_balance: balanceResponse.balance });
          setIsFirstOrder((balanceResponse.orders_count || 0) === 0);
          logger.log('✅ Баланс загружен:', balanceResponse.balance, '₽, заказов:', balanceResponse.orders_count || 0);
        }

        // Товары
        setAllProducts(productsData.products || []);
        logger.log(`✅ Загружено ${productsData.products?.length || 0} товаров`);

        // Сториз (неблокирующий)
        botAPI.getStories().then(data => {
          setStories(data.stories || []);
          logger.log(`✅ Загружено ${data.stories?.length || 0} сториз`);
        }).catch(err => {
          console.warn('⚠️ Не удалось загрузить сториз:', err);
        });

        // Загружаем корзину (БЕЗ привязки к району)
        try {
          const savedCart = CartStorage.loadCart();
          if (savedCart && savedCart.length > 0) {
            setCartItems(savedCart);
            logger.log('📦 Восстановлена корзина:', savedCart.length, 'товаров');
          }
        } catch (err) {
          console.warn('⚠️ Не удалось загрузить корзину:', err);
          setCartItems([]);
        }
        setIsCartLoaded(true);
        
        // Диагностика API (в dev режиме)
        // ОТКЛЮЧЕНО: загружает все товары без фильтра
        // if (TelegramWebAppUtils.isDev()) {
        //   await APIUtils.diagnose();
        // }

        logger.log('✅ Приложение инициализировано');
        setIsLoading(false);
        
      } catch (err) {
        console.error('❌ Ошибка инициализации:', err);
        setError(APIUtils.formatError(err));
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Глобальный цикл - прогресс по районам не отслеживается
  // useEffect для установки прогресса удалён (не нужен для глобального цикла)

  // Обработка кнопки "Назад" в Telegram/Max (no-op в standalone — браузер имеет свою кнопку)
  useEffect(() => {
    if (currentPage === 'home') {
      Platform.hideBackButton();
    } else {
      Platform.showBackButton(() => navigateTo('home'));
    }
  }, [currentPage, navigateTo]);

  // Скрываем главную кнопку Telegram/Max
  useEffect(() => {
    Platform.hideMainButton();
    // Дополнительная попытка скрыть через небольшую задержку
    setTimeout(() => {
      Platform.hideMainButton();
    }, 100);
  }, []);

  // Скрываем кнопку при каждом изменении страницы
  useEffect(() => {
    Platform.hideMainButton();
    if (currentPage === 'cart') {
      Platform.hideMainButton();
      setTimeout(() => Platform.hideMainButton(), 50);
      setTimeout(() => Platform.hideMainButton(), 100);
      setTimeout(() => Platform.hideMainButton(), 200);
    }
  }, [currentPage]);

  // Добавление товара в корзину
  const handleAddToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  };

  // Сохраняем корзину при каждом изменении (localStorage + БД)
  // Глобальный цикл - без привязки к району
  useEffect(() => {
    if (!isCartLoaded) return; // Не затирать localStorage до загрузки корзины
    if (cartItems.length >= 0) {
      // Сохраняем в localStorage (мгновенно)
      CartStorage.saveCart(cartItems);

      // Сохраняем в БД (с debounce через setTimeout)
      const saveTimer = setTimeout(async () => {
        if (!telegramUser?.id) {
          logger.log('⚠️ Пропускаем сохранение в БД: нет telegramUser.id');
          return;
        }

        try {
          // Для глобального цикла cycle_id определится автоматически на бэкенде
          await botAPI.saveCartSnapshot(
            telegramUser.id,
            null, // cycle_id - определится автоматически
            cartItems
          );
          logger.log('💾 Корзина сохранена в БД (глобальный цикл):', cartItems.length, 'товаров');
        } catch (err) {
          console.error('❌ Ошибка сохранения корзины в БД:', err);
        }
      }, 1000); // Debounce 1 секунда

      return () => clearTimeout(saveTimer);
    }
  }, [cartItems, telegramUser, isCartLoaded]);

  // Обновление количества товара
  const handleUpdateQuantity = (id, quantity) => {
    setCartItems(prev =>
      prev.map(item => (item.id === id ? { ...item, quantity } : item))
    );
  };

  // Удаление товара из корзины
  const handleRemoveFromCart = (id) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  // Очистка корзины (например, после оформления заказа)
  const handleClearCart = () => {
    setCartItems([]);
    CartStorage.clearCart(); // Глобальный цикл - без привязки к району
  };

  // Обработка оформления заказа через Telegram Web App
  const handleOrderSubmit = async (contactData = {}) => {
    logger.log('📦 handleOrderSubmit получил contactData:', contactData);
    
    try {
      Platform.hapticFeedback('medium');
      
      // Проверяем наличие обязательных данных
      if (!contactData || !contactData.phone || !contactData.address) {
        throw new Error('Не указаны контактные данные');
      }
      
      // Используем имя из формы или из Telegram
      const customerName = contactData.name ||
        (telegramUser ? `${telegramUser.first_name} ${telegramUser.last_name || ''}`.trim() : null);

      if (!cartItems || cartItems.length === 0) {
        throw new Error('Корзина пуста');
      }

      // Подготовка данных заказа (глобальный цикл - без указания района)
      // Нормализация цен (каталог хранит цену в разных полях: price, current_price, basePricePerKg)
      const normalizedItems = cartItems.map(item => ({
        ...item,
        price: item.price || item.current_price || item.basePricePerKg || 0
      }));

      const orderData = {
        items: normalizedItems.map(item => ({
          product_id: String(item.id),
          product_name: item.name,
          quantity: Number(item.quantity),
          price: Number(item.price),
          total: Number(item.price) * Number(item.quantity)
        })),
        total_amount: normalizedItems.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0),
        contact_info: {
          telegram_user_id: telegramUser?.id || null,
          username: telegramUser?.username || '',
          full_name: customerName || '',
          phone: contactData.phone,
          address: contactData.address
        },
        is_organizer: contactData.isOrganizer || false,
        is_subscribed_to_promo: contactData.isSubscribedToPromo || false,
        promo_code: contactData.promoCode || null,
        use_balance_amount: contactData.balanceToUse || 0
      };

      logger.log('📦 Отправка заказа:', JSON.stringify(orderData, null, 2));

      // Проверяем соединение с сервером
      const isConnected = await APIUtils.checkConnection();
      if (!isConnected) {
        throw new Error('Сервер недоступен. Проверьте, что backend запущен на порту 8000');
      }

      // Отправляем заказ на сервер
      const result = await botAPI.createOrder(orderData);
      
      logger.log('✅ Результат создания заказа:', result);
      
      // Проверяем успешность операции
      if (!result || !result.success) {
        throw new Error(result?.message || 'Не удалось создать заказ');
      }
      
      // Очищаем корзину сразу после создания заказа (до оплаты)
      handleClearCart();

      // Уведомляем о успешной отправке через Telegram/Max Web App
      if (Platform.isTelegram() || Platform.isMax()) {
        Platform.sendData({
          type: 'order_created',
          order_id: result.order_id,
          total: orderData.total_amount
        });
      }

      // Предлагаем оплатить заказ
      Platform.showConfirm(
        `Заказ №${result.order_id} создан!\n\nОплатить сейчас?`,
        async (confirmed) => {
          if (confirmed) {
            try {
              const returnUrl = `${window.location.origin}/payment/status`;
              const paymentResult = await botAPI.createPayment(result.order_id, returnUrl);

              if (paymentResult?.success && paymentResult.confirmation_url) {
                // Save payment info for status page
                sessionStorage.setItem('pending_payment_id', paymentResult.payment_id);
                sessionStorage.setItem('pending_order_id', String(result.order_id));
                // Redirect to YooKassa payment page
                window.location.href = paymentResult.confirmation_url;
              } else {
                Platform.showAlert('Не удалось создать платёж. Вы можете оплатить позже в разделе «Мои заказы».');
                navigateTo('home');
              }
            } catch (payErr) {
              console.error('Payment creation failed:', payErr);
              Platform.showAlert('Не удалось создать платёж. Вы можете оплатить позже в разделе «Мои заказы».');
              navigateTo('home');
            }
          } else {
            // Пользователь отказался от оплаты — на главную
            Platform.showAlert(`Заказ №${result.order_id} принят! Оплатить можно позже в разделе «Мои заказы».`);
            navigateTo('home');
          }
        }
      );
      
    } catch (error) {
      console.error('❌ Ошибка оформления заказа:', error);
      
      // Детальная информация об ошибке
      let errorMessage = 'Не удалось оформить заказ';
      
      if (error.response) {
        // Ошибка от сервера
        console.error('Ответ сервера:', error.response);
        errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
      } else if (error.message) {
        // Обычная ошибка
        errorMessage = error.message;
      }
      
      // Показываем ошибку пользователю
      Platform.showAlert(`Ошибка: ${errorMessage}`);
      Platform.hapticFeedback('error');
      
      // Пробрасываем ошибку дальше для обработки в CheckoutForm
      throw error;
    }
  };

  // Standalone login gate — show login screen for browser users
  if (needsLogin) {
    return (
      <StandaloneLogin
        onLoginSuccess={(user) => {
          if (!user) {
            // Guest mode — remember for session and reload to continue without auth
            sessionStorage.setItem('guest_mode', '1');
          } else {
            applyPendingReferral();
          }
          setNeedsLogin(false);
          setIsLoading(true);
          // Re-trigger full init after login
          window.location.reload();
        }}
      />
    );
  }

  // Экран загрузки
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-4 bg-gray-50 dark:bg-gray-900">
        <div className="text-center animate-fadeIn">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-700 dark:text-gray-300 text-base mt-4">
            Загрузка магазина...
          </p>
          {telegramUser && (
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-1">
              {telegramUser.first_name ? 
                `Привет, ${telegramUser.first_name}!` : 
                'Привет, ценитель морских деликатесов!'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Экран ошибки
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-4 bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl max-w-sm w-full animate-fadeIn">
          <div className="text-center">
            <div className="flex justify-center mb-4"><AlertCircle size={48} className="text-gray-400" strokeWidth={1.5} /></div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Что-то пошло не так
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-base mb-6">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 hover:scale-105"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Auth state for route decisions (standalone only)
  const isAuthenticated = !!Platform.getAuthToken() || sessionStorage.getItem('guest_mode') === '1';

  const handleStartGuest = () => {
    sessionStorage.setItem('guest_mode', '1');
    navigateTo('catalog');
  };

  // Page elements (shared between router and step-based modes)
  const homePageElement = (
    <HomePage
      products={allProducts}
      stories={stories}
      categories={categories}
      cycleInfo={cycleInfo}
      onNavigateToCart={() => { navigateTo('cart'); Platform.hapticFeedback('light'); }}
      onNavigateToCatalog={() => { navigateTo('catalog'); Platform.hapticFeedback('light'); }}
      onViewDetails={(product) => { setSelectedProduct(product); Platform.hapticFeedback('light'); }}
      progress={progress}
      currentPage={currentPage}
      onNavigate={navigateTo}
      cartItemsCount={cartItems.length}
      cartItems={cartItems}
      onAddToCart={handleAddToCart}
      onUpdateQuantity={handleUpdateQuantity}
      onRemoveFromCart={(id) => { handleRemoveFromCart(id); Platform.hapticFeedback('light'); }}
      isAuthenticated={isAuthenticated}
      onStartGuest={handleStartGuest}
    />
  );

  const catalogElement = (
    <CatalogHybrid
      progress={progress}
      cycleInfo={cycleInfo}
      currentPage={currentPage}
      onNavigate={navigateTo}
      cartItemsCount={cartItems.length}
      cartTotal={cartItems.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0)}
      cartItems={cartItems}
      onUpdateQuantity={handleUpdateQuantity}
      onRemoveFromCart={(id) => { handleRemoveFromCart(id); Platform.hapticFeedback('light'); }}
      onAddToCart={(product, weight) => {
        const quantity = weight?.value || 1;
        handleAddToCart(product, quantity);
        Platform.hapticFeedback('light');
      }}
      onViewDetails={(product) => { setSelectedProduct(product); Platform.hapticFeedback('light'); }}
    />
  );

  const cartElement = (
    <CartComponent
      cartItems={cartItems}
      onUpdateQuantity={handleUpdateQuantity}
      onRemove={(id) => { handleRemoveFromCart(id); Platform.hapticFeedback('light'); }}
      onAddToCart={handleAddToCart}
      onGoToCatalog={() => navigateTo('catalog')}
      onClearCart={handleClearCart}
      telegramUser={telegramUser}
      allProducts={allProducts}
      onCheckout={handleOrderSubmit}
      currentPage={currentPage}
      onNavigate={navigateTo}
      cartItemsCount={cartItems.length}
      isFirstOrder={isFirstOrder}
    />
  );

  const profileElement = (
    <ProfilePage
      telegramUser={telegramUser}
      onClose={() => { navigateTo('home'); Platform.hapticFeedback('light'); }}
      currentPage={currentPage}
      onNavigate={navigateTo}
      cartItemsCount={cartItems.length}
    />
  );

  // Apply pending referral after auth
  const applyPendingReferral = async () => {
    try {
      const refCode = sessionStorage.getItem('pending_referral');
      if (!refCode) return;

      const result = await botAPI.applyReferral(refCode, 'web');
      sessionStorage.removeItem('pending_referral');

      if (result.success) {
        setTimeout(() => {
          alert(`Вам начислен приветственный бонус ${result.welcome_bonus || 100} руб!`);
        }, 1000);
      }
    } catch (e) {
      sessionStorage.removeItem('pending_referral');
      console.log('Referral apply skipped:', e.message);
    }
  };

  const handleLoginSuccess = (user) => {
    if (!user) {
      // Guest mode — навигация на главную без перезагрузки
      sessionStorage.setItem('guest_mode', '1');
      setNeedsLogin(false);
      navigateTo('home');
      return;
    }
    // Реальный логин — сохраняем юзера и перезагружаем на профиль
    Platform.saveUser(user);
    applyPendingReferral();
    setNeedsLogin(false);
    setIsLoading(true);
    window.location.href = '/profile';
  };

  // Основной UI приложения (dual-mode: URL routing for standalone, step-based for Telegram/Max)
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-sky-600 focus:text-white focus:font-semibold focus:shadow-lg"
      >
        К содержимому
      </a>
      <WelcomeBanner telegramUser={telegramUser} balance={userBalance} />

      <main id="main-content" role="main" tabIndex={-1}>
      {isRouterMode ? (
        <Routes>
          <Route path="/" element={
            homePageElement
          } />
          <Route path="/catalog" element={catalogElement} />
          <Route path="/cart" element={cartElement} />
          <Route path="/profile" element={
            isAuthenticated ? profileElement : <Navigate to="/login" replace />
          } />
          <Route path="/login" element={
            <div>
              <GlobalHeader location="Москва и МО" currentPage="login" onNavigate={navigateTo} cartItemsCount={cartItems.length} />
              <StandaloneLogin onLoginSuccess={handleLoginSuccess} />
            </div>
          } />
          <Route path="/privacy" element={
            <PrivacyPolicyPage onBack={() => navigateTo('home')} />
          } />
          <Route path="/payment/status" element={
            <PaymentStatusPage onNavigate={navigateTo} />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <>
          {currentPage === 'home' && homePageElement}
          {currentPage === 'catalog' && catalogElement}
          {currentPage === 'cart' && cartElement}
          {currentPage === 'profile' && profileElement}
          {currentPage === 'privacy' && <PrivacyPolicyPage onBack={() => navigateTo('home')} />}
          {currentPage === 'payment_status' && <PaymentStatusPage onNavigate={navigateTo} />}
        </>
      )}
      </main>

      <Footer onNavigate={navigateTo} />

      <BottomNavigation
        currentPage={currentPage}
        onNavigate={navigateTo}
        cartItemsCount={cartItems.length}
        hidden={selectedProduct !== null}
      />

      <CookieBanner />

      <ProductDetailsModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={handleAddToCart}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveFromCart={handleRemoveFromCart}
        cartItems={cartItems}
      />
    </div>
  );
};

export default App;
